import type { ExplorationFilter, ExplorationVolume } from '../types/exploration';
import { searchVolumes } from './fuzzyMatchVolume';

export function clusterFilterKey(v: ExplorationVolume): string {
  return v.cluster ? v.cluster : v.instanceId ? 'EC2 Instances' : 'Unattached';
}

export function infrastructureBucket(v: ExplorationVolume): 'cluster' | 'ec2' | 'unattached' {
  return v.cluster ? 'cluster' : v.instanceId ? 'ec2' : 'unattached';
}

function getVolFieldValue(v: ExplorationVolume, type: string, key?: string): string {
  switch (type) {
    case 'cluster':
      return clusterFilterKey(v);
    case 'infrastructure':
      return infrastructureBucket(v);
    case 'region':
      return v.region || '(unknown)';
    case 'instanceId':
      return v.instanceId || '(unattached)';
    case 'nodeGroup':
      return v.nodeGroup || '(No Group)';
    case 'diskType':
      return v.diskType || '(unknown)';
    case 'volumeId':
      return v.ebsId || '(unknown)';
    case 'tag':
      return (v.tags && key && v.tags[key]) || '(untagged)';
    case 'activationStatus':
      return v.activationStatus;
    case 'supportStatus':
      return v.supportStatus;
    case 'accountId':
      return v.accountId;
    default:
      return '';
  }
}

export function countActiveFilters(filters: ExplorationFilter[]): number {
  return filters.length;
}

export function filterVolumes(
  volumes: ExplorationVolume[],
  filters: ExplorationFilter[],
  searchQuery: string
): ExplorationVolume[] {
  let vols = volumes;

  if (filters.length) {
    const cap = filters.find((f): f is Extract<ExplorationFilter, { type: 'capacity' }> => f.type === 'capacity');
    if (cap) {
      vols = vols.filter((v) => {
        const tib = (v.size || 0) / 1024;
        return tib >= cap.min && tib <= cap.max;
      });
    }

    const groups = new Map<string, { type: string; key?: string; values: Set<string> }>();
    filters.forEach((f) => {
      if (f.type === 'capacity') return;
      const gk = `${f.type}|${'key' in f ? f.key || '' : ''}`;
      if (!groups.has(gk)) {
        groups.set(gk, {
          type: f.type,
          key: 'key' in f ? f.key : undefined,
          values: new Set(),
        });
      }
      groups.get(gk)!.values.add(f.value);
    });

    groups.forEach((g) => {
      vols = vols.filter((v) => g.values.has(getVolFieldValue(v, g.type, g.key)));
    });
  }

  vols = searchVolumes(vols, searchQuery);

  return vols;
}

export function collectTagKeys(volumes: ExplorationVolume[]): string[] {
  const keys = new Set<string>();
  volumes.forEach((v) => Object.keys(v.tags || {}).forEach((k) => keys.add(k)));
  return [...keys];
}
